import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Code2, Smartphone, BrainCircuit, TrendingUp, PenTool } from "lucide-react";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const ICONS = {
  "software-web-development": Code2,
  "mobile-app-development": Smartphone,
  "ai-automation": BrainCircuit,
  "seo-digital-growth": TrendingUp,
  "uiux-creative-design": PenTool,
};

const CARDS = [
  {
    slug: "software-web-development",
    name: "Software & Web Development",
    tagline: "Our core craft",
    blurb:
      "Custom software, websites, web applications, SaaS products, e-commerce builds and API integrations — engineered, not assembled.",
    points: ["Custom software & SaaS", "Web apps & e-commerce", "APIs, MVPs & ongoing support"],
    featured: true,
  },
  {
    slug: "mobile-app-development",
    name: "Mobile App Development",
    blurb: "iOS, Android and cross-platform apps with design that earns its place on the home screen.",
  },
  {
    slug: "ai-automation",
    name: "AI & Automation",
    blurb: "AI integrations, chatbots and workflow automation that hand hours back to your team.",
  },
  {
    slug: "seo-digital-growth",
    name: "SEO & Digital Growth",
    blurb: "Technical SEO, GEO, paid media and CRO — the multiplier once the product deserves it.",
  },
  {
    slug: "uiux-creative-design",
    name: "UI/UX & Creative Design",
    blurb: "Product design, brand identity and interfaces built around how people actually behave.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BentoCard = ({ card, index }) => {
  const Icon = ICONS[card.slug];
  return (
    <motion.div
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      custom={index}
      className={cn(
        "md:col-span-3 lg:col-span-4",
        card.featured && "md:col-span-6 md:row-span-1 lg:col-span-8"
      )}
    >
      <Link
        to={`/services/${card.slug}`}
        data-testid={`bento-card-${card.slug}`}
        className={cn(
          "group relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-brand-mist/12 bg-brand-charcoal p-6 transition-all duration-300 ease-brand hover:border-brand-emerald/35 sm:p-8",
          card.featured && "min-h-[22rem]"
        )}
      >
        {card.featured && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-25 transition-opacity duration-500 group-hover:opacity-40"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNoJTIwZ2VvbWV0cmljJTIwZ2xvdyUyMDNkJTIwbGluZXN8ZW58MHx8fHwxNzkzNzUyMjU1fDA&ixlib=rb-4.1.0&q=85)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              maskImage: "linear-gradient(to top, black 20%, transparent 85%)",
              WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 85%)",
            }}
          />
        )}
        <div className="relative flex items-start justify-between">
          <span className="rounded-lg border border-brand-emerald/25 bg-brand-emerald/10 p-2.5 text-brand-emerald">
            <Icon size={20} />
          </span>
          <ArrowUpRight
            size={18}
            className="text-brand-mist transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-emerald"
          />
        </div>
        <div className="relative mt-10 space-y-3">
          {card.tagline && <p className="eyebrow">{card.tagline}</p>}
          <h3
            className={cn(
              "font-display font-semibold text-brand-snow",
              card.featured ? "text-2xl sm:text-3xl" : "text-xl"
            )}
          >
            {card.name}
          </h3>
          <p className="max-w-md text-sm leading-relaxed text-brand-mist">{card.blurb}</p>
          {card.points && (
            <ul className="flex flex-wrap gap-2 pt-1">
              {card.points.map((p) => (
                <li
                  key={p}
                  className="rounded-full border border-brand-mist/15 px-3 py-1 text-[11px] text-brand-mist"
                >
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

const ServicesBento = () => {
  // Names, blurbs and counts come from the services API; local CARDS are the fallback.
  const [apiCategories, setApiCategories] = useState(null);

  useEffect(() => {
    api
      .get("/services/categories")
      .then((r) => setApiCategories(r.data))
      .catch(() => {});
  }, []);

  const cards = CARDS.map((c) => {
    const match = apiCategories?.find((a) => a.slug === c.slug);
    return match ? { ...c, name: match.name, blurb: match.blurb } : c;
  });

  return (
    <Section testid="services-bento-section">
      <Container className="space-y-12">
        <SectionHeading
          eyebrow="What we do"
          title="Five disciplines, one accountable team"
          description="Technology is our core — roughly seventy per cent of the work. Digital growth is the multiplier we apply on top of it."
          testid="bento-heading"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-6 lg:grid-cols-12" data-testid="services-bento-grid">
          {cards.map((card, i) => (
            <BentoCard key={card.slug} card={card} index={i} />
          ))}
        </div>
      </Container>
    </Section>
  );
};

export default ServicesBento;
